import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFussstatusGrafik } from "../../context/FussstatusGrafikContext";
import { useFussstatus } from "../../context/FussstatusContext";
import { sanitizeUlcusFindings } from "../Fussstatus/Fussstatus";
import { useAllgemeineDaten } from "../../context/AllgemeineDatenContext";
import styles from "./FussstatusGrafik.module.css";

import medialR from "../../assets/footViews/medialR.png";
import medialL from "../../assets/footViews/medialL.png";
import lateralR from "../../assets/footViews/lateralR.png";
import lateralL from "../../assets/footViews/lateralL.png";
import plantarR from "../../assets/footViews/plantarR.png";
import plantarL from "../../assets/footViews/plantarL.png";
import dorsalR from "../../assets/footViews/dorsalR.png";
import dorsalL from "../../assets/footViews/dorsalL.png";

const FussstatusGrafik = () => {
  const navigate = useNavigate();

    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
    
  const { data, saveFinding, deleteFinding, setData: setGrafikData } = useFussstatusGrafik();
  const { data: fussData } = useFussstatus();

  // On mount: remove any stale ulcus findings that survived a page refresh.
  useEffect(() => {
    setGrafikData((prev) => sanitizeUlcusFindings(prev, fussData));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const { data: allgemeineData } = useAllgemeineDaten();
  const fussAuswahl = allgemeineData?.fussAuswahl;
  
  const showLeftFoot = fussAuswahl !== "rechts";
  const showRightFoot = fussAuswahl !== "links";
  const showBothFeet = showLeftFoot && showRightFoot;

  // const blockAllExceptHVandKrallenRechts = fussData.rechtsPraeulzerativeVeranderungen === "nein";
  // const blockAllExceptHVandKrallenLinks   = fussData.linksPraeulzerativeVeranderungen  === "nein";
  const blockAllExceptHVandKrallenRechts = false;
  const blockAllExceptHVandKrallenLinks   = false;

  // ── Amputation Blocking Logic ─────────────────────────────────────
  const isLeftAmputated  = fussData.linksZustandNachAmputation  === "ja";
  const isRightAmputated = fussData.rechtsZustandNachAmputation === "ja";

// ── Toe Amputation Blocking ─────────────────────
const leftAmputatedToes  = fussData.linksZehenamputation  || [];
const rightAmputatedToes = fussData.rechtsZehenamputation || [];

// Helper function
const isToeAmputated = (side, toe) => {
  if (side === "left")  return leftAmputatedToes.includes(toe);
  if (side === "right") return rightAmputatedToes.includes(toe);
  return false;
};

// ── Ulcus blocking logic per side ────────────────────────────────────────
const ulcusTypLeft  = fussData.linksUlcus  === "ja" ? fussData.linksUlcusTyp  || "" : "";
const ulcusTypRight = fussData.rechtsUlcus === "ja" ? fussData.rechtsUlcusTyp || "" : "";

const allowAkutLeft    = ulcusTypLeft.includes("akut");
const allowAbgeheiltLeft  = ulcusTypLeft.includes("abgeheilt");
const allowAkutRight   = ulcusTypRight.includes("akut");
const allowAbgeheiltRight = ulcusTypRight.includes("abgeheilt");

// If "nein" → both false → both blocked (correct behavior)
  const blockHVandKrallenRechts = fussData.rechtsFussdeformitaeten === "nein";
  const blockHVandKrallenLinks   = fussData.linksFussdeformitaeten  === "nein";

// ── Per-foot Ulcus requirement validation ─────────────────────────────────
const requiresAkutLeft      = fussData.linksUlcus  === "ja" && allowAkutLeft;
const requiresAbgeheiltLeft = fussData.linksUlcus  === "ja" && allowAbgeheiltLeft;
const requiresAkutRight     = fussData.rechtsUlcus === "ja" && allowAkutRight;
const requiresAbgeheiltRight= fussData.rechtsUlcus === "ja" && allowAbgeheiltRight;

const leftFindings  = [...data.medLeft, ...data.latLeft, ...data.plLeft, ...data.dorsLeft];
const rightFindings = [...data.medRight, ...data.latRight, ...data.plRight, ...data.dorsRight];

const hasAkutLeft      = leftFindings.some(item => item.befund === "Ulcus akut");
const hasAbgeheiltLeft = leftFindings.some(item => item.befund === "Ulcus abgeheilt");
const hasAkutRight     = rightFindings.some(item => item.befund === "Ulcus akut");
const hasAbgeheiltRight= rightFindings.some(item => item.befund === "Ulcus abgeheilt");

// Validation messages per foot (displayed in every view of that foot)
const leftErrorMessages = [];
if (requiresAkutLeft && !hasAkutLeft) {
  leftErrorMessages.push("Da „akut“ ausgewählt wurde, muss mindestens ein „Ulcus akut“ markiert werden.");
}
if (requiresAbgeheiltLeft && !hasAbgeheiltLeft) {
  leftErrorMessages.push("Da „abgeheilt“ ausgewählt wurde, muss mindestens ein „Ulcus abgeheilt“ markiert werden.");
}

const rightErrorMessages = [];
if (requiresAkutRight && !hasAkutRight) {
  rightErrorMessages.push("Da „akut“ ausgewählt wurde, muss mindestens ein „Ulcus akut“ markiert werden.");
}
if (requiresAbgeheiltRight && !hasAbgeheiltRight) {
  rightErrorMessages.push("Da „abgeheilt“ ausgewählt wurde, muss mindestens ein „Ulcus abgeheilt“ markiert werden.");
}

const hasErrors = leftErrorMessages.length > 0 || rightErrorMessages.length > 0;
  // ───────────────────────────────────────────────────────────────────

  /* ----- MEDIAL ----- */
  const lokalisationRight = ["VRM", "MRM", "RRM"];
  const lokalisationLeft  = ["VLM", "MLM", "RLM"];
  /* ----- LATERAL ----- */
  const lateralRight = ["VRL", "MRL", "RRL"];
  const lateralLeft  = ["VLL", "MLL", "RLL"];
  /* ----- PLANTAR ----- */
  const plantarRight = ["D1","D2","D3","D4","D5","M1","M2","M3","M4","M5","B5","Ferse"];
  const plantarLeft  = ["D1","D2","D3","D4","D5","M1","M2","M3","M4","M5","B5","Ferse"];
  /* ----- DORSAL ----- */
  const dorsalRight = ["D1","D2","D3","D4","D5","M1","M2","M3","M4","M5"];
  const dorsalLeft  = ["D1","D2","D3","D4","D5","M1","M2","M3","M4","M5"];

const getBefundOptions = (isPlantar = false) => [
  "Druckstelle / Blase / Hautrötung",
  "Schwielenbildung",
  "Einblutung",
  "Rhagade",
  "Hallux valgus",
  "Ulcus akut",
  "Ulcus abgeheilt",
  "Narbe",
  "Krallen-/Hammerzehe",
  ...(isPlantar ? ["Charcot Prominenz"] : []),
];

//  Check if a specific ulcus type is already saved in a localisation 
const isUlcusAkutSaved = (viewKey, loc) => {
  return data[viewKey]?.some(item => item.loc === loc && item.befund === "Ulcus akut");
};

const isUlcusAbgeheiltSaved = (viewKey, loc) => {
  return data[viewKey]?.some(item => item.loc === loc && item.befund === "Ulcus abgeheilt");
};


const getUlcusDisabled = (befund, loc, viewKey) => {
  if (!loc) return false; // no loc selected → don't block yet

  // Only apply this restriction in plantar & dorsal views
  const isRelevantView = ["plRight", "plLeft", "dorsRight", "dorsLeft"].includes(viewKey);
  if (!isRelevantView) return false;

  if (befund === "Ulcus akut") {
    return isUlcusAbgeheiltSaved(viewKey, loc);
  }
  if (befund === "Ulcus abgeheilt") {
    return isUlcusAkutSaved(viewKey, loc);
  }
  return false;
};

  /* ----- TEMPORARY FORM STATES (current selection) ----- */
  const [rightLoc, setRightLoc] = useState("");
  const [rightBefund, setRightBefund] = useState("");
  const [leftLoc, setLeftLoc] = useState("");
  const [leftBefund, setLeftBefund] = useState("");
  const [latRightLoc, setLatRightLoc] = useState("");
  const [latRightBefund, setLatRightBefund] = useState("");
  const [latLeftLoc, setLatLeftLoc] = useState("");
  const [latLeftBefund, setLatLeftBefund] = useState("");
  const [plRightLoc, setPlRightLoc] = useState("");
  const [plRightBefund, setPlRightBefund] = useState("");
  const [plLeftLoc, setPlLeftLoc] = useState("");
  const [plLeftBefund, setPlLeftBefund] = useState("");
  const [dorsRightLoc, setDorsRightLoc] = useState("");
  const [dorsRightBefund, setDorsRightBefund] = useState("");
  const [dorsLeftLoc, setDorsLeftLoc] = useState("");
  const [dorsLeftBefund, setDorsLeftBefund] = useState("");

//clear Befund if Hallux valgus not in M1
  React.useEffect(() => {
    if (plRightBefund === "Hallux valgus" && plRightLoc !== "M1") setPlRightBefund("");
    if (plLeftBefund  === "Hallux valgus" && plLeftLoc  !== "M1") setPlLeftBefund("");
    if (dorsRightBefund === "Hallux valgus" && dorsRightLoc !== "M1") setDorsRightBefund("");
    if (dorsLeftBefund  === "Hallux valgus" && dorsLeftLoc  !== "M1") setDorsLeftBefund("");
  }, [
    plRightLoc, plRightBefund,
    plLeftLoc, plLeftBefund,
    dorsRightLoc, dorsRightBefund,
    dorsLeftLoc, dorsLeftBefund,
  ]);


  // clear Befund if Krallen-/Hammerzehe not in D1–D5
React.useEffect(() => {
  const isToe = loc => ["D1", "D2", "D3", "D4", "D5"].includes(loc);

  if (plRightBefund  === "Krallen-/Hammerzehe" && !isToe(plRightLoc))  setPlRightBefund("");
  if (plLeftBefund   === "Krallen-/Hammerzehe" && !isToe(plLeftLoc))   setPlLeftBefund("");
  if (dorsRightBefund === "Krallen-/Hammerzehe" && !isToe(dorsRightLoc)) setDorsRightBefund("");
  if (dorsLeftBefund  === "Krallen-/Hammerzehe" && !isToe(dorsLeftLoc))  setDorsLeftBefund("");
}, [
  plRightLoc,  plRightBefund,
  plLeftLoc,   plLeftBefund,
  dorsRightLoc, dorsRightBefund,
  dorsLeftLoc,  dorsLeftBefund,
]);


// Clear Ulcus Befund if it becomes invalid because the opposite type is already saved
// for the currently selected localisation (Plantar & Dorsal only)
React.useEffect(() => {
  const checkAndClearUlcus = (loc, befund, setBefund, viewKey) => {
    if (!loc || !befund) return;
    if (!["plRight", "plLeft", "dorsRight", "dorsLeft"].includes(viewKey)) return;

    const isAkutSelected     = befund === "Ulcus akut";
    const isAbgeheiltSelected = befund === "Ulcus abgeheilt";

    if (!isAkutSelected && !isAbgeheiltSelected) return;

    const oppositeIsSaved = isAkutSelected
      ? isUlcusAbgeheiltSaved(viewKey, loc)
      : isUlcusAkutSaved(viewKey, loc);

    if (oppositeIsSaved) {
      setBefund("");
    }
  };

  checkAndClearUlcus(plRightLoc,   plRightBefund,   setPlRightBefund,   "plRight");
  checkAndClearUlcus(plLeftLoc,    plLeftBefund,    setPlLeftBefund,    "plLeft");
  checkAndClearUlcus(dorsRightLoc, dorsRightBefund, setDorsRightBefund, "dorsRight");
  checkAndClearUlcus(dorsLeftLoc,  dorsLeftBefund,  setDorsLeftBefund,  "dorsLeft");
}, [
  // Location changes trigger check
  plRightLoc, plLeftLoc, dorsRightLoc, dorsLeftLoc,
  // Current befund values
  plRightBefund, plLeftBefund, dorsRightBefund, dorsLeftBefund,
  // Data changes (save/delete) can make something newly blocked
  data.plRight, data.plLeft, data.dorsRight, data.dorsLeft,
]);

  return (
    <div className={styles.page}>
      <h1>Fußstatus</h1>

      {/* ---------------------- MEDIAL ---------------------- */}
      <h2 className={styles.subTitle}>Medial</h2>
      <div className={styles.container}>
        {showRightFoot && (
        <div className={styles.footSection}>
          <img src={medialR} alt="medial right" className={styles.medialImage} />
          <select value={rightLoc} onChange={e => setRightLoc(e.target.value)} className={styles.dropdown}>
            <option value="">Lokalisation</option>
            {lokalisationRight.map(o => (
              <option 
                key={o} 
                value={o}
                disabled={isRightAmputated && o === "VRM"}
              >
                {o}
              </option>
            ))}
          </select>
          <select
            value={rightBefund}
            onChange={e => setRightBefund(e.target.value)}
            className={styles.dropdown}
          >
            <option value="">Befund</option>
            {getBefundOptions().map(o => (
              <option
                key={o}
                value={o}
                disabled={
                  (o === "Ulcus akut"      && !allowAkutRight) ||
                  (o === "Ulcus abgeheilt" && !allowAbgeheiltRight) ||
                  (blockAllExceptHVandKrallenRechts && !["Hallux valgus", "Krallen-/Hammerzehe"].includes(o)) ||
                  (o === "Hallux valgus"   && rightLoc !== "M1") ||
                  (o === "Krallen-/Hammerzehe" && !["D1","D2","D3","D4","D5"].includes(rightLoc)) ||
                  (blockHVandKrallenRechts && (o === "Hallux valgus" || o === "Krallen-/Hammerzehe"))
                }
              >
                {o}
              </option>
            ))}
          </select>
          <button
            className={styles.saveButton}
            disabled={!rightLoc || !rightBefund}
            onClick={() => {
              saveFinding("medRight", rightLoc, rightBefund);
              setRightLoc("");
              setRightBefund("");
            }}
          >
            Speichern
          </button>
          <div className={styles.savedList}>
            <p className={styles.hint}>Hinweis: Pro Lokalisation können mehrere Befunde gespeichert werden</p>
            {rightErrorMessages.length > 0 && (
            <div className={styles["validation-errors"]}>
              {rightErrorMessages.map((msg, idx) => (
                <p key={idx} className={styles["error-text"]}>
                  {msg}
                </p>
              ))}
          </div>
          )}  
            <h4>Rechts – gespeicherte Befunde:</h4>
            <div className={styles.matrix}>
              {data.medRight.map((item, index) => (
                <div key={index} className={styles.savedItem}>
                  <span>{item.loc} – {item.befund}</span>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => deleteFinding("medRight", index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div> )}
        
        {showLeftFoot && (
        <div className={styles.footSection}>
          <img src={medialL} alt="medial left" className={styles.medialImage} />
          <select value={leftLoc} onChange={e => setLeftLoc(e.target.value)} className={styles.dropdown}>
            <option value="">Lokalisation</option>
            {lokalisationLeft.map(o => (
              <option 
                key={o} 
                value={o}
                disabled={isLeftAmputated && o === "VLM"}
              >
                {o}
              </option>
            ))}
          </select>
          <select
            value={leftBefund}
            onChange={e => setLeftBefund(e.target.value)}
            className={styles.dropdown}
          >
            <option value="">Befund</option>
            {getBefundOptions().map(o => (
              <option
                key={o}
                value={o}
                disabled={
                  (o === "Ulcus akut"      && !allowAkutLeft) ||
                  (o === "Ulcus abgeheilt" && !allowAbgeheiltLeft) ||
                  (blockAllExceptHVandKrallenLinks && !["Hallux valgus", "Krallen-/Hammerzehe"].includes(o)) ||
                  (o === "Hallux valgus"   && leftLoc !== "M1") ||
                  (o === "Krallen-/Hammerzehe" && !["D1","D2","D3","D4","D5"].includes(leftLoc)) ||
                  (blockHVandKrallenLinks && (o === "Hallux valgus" || o === "Krallen-/Hammerzehe"))
                }
              >
                {o}
              </option>
            ))}
          </select>
          <button
            className={styles.saveButton}
            disabled={!leftLoc || !leftBefund}
            onClick={() => {
              saveFinding("medLeft", leftLoc, leftBefund);
              setLeftLoc("");
              setLeftBefund("");
            }}
          >
            Speichern
          </button>
          <div className={styles.savedList}>
            <p className={styles.hint}>Hinweis: Pro Lokalisation können mehrere Befunde gespeichert werden</p>
            {leftErrorMessages.length > 0 && (
            <div className={styles["validation-errors"]}>
              {leftErrorMessages.map((msg, idx) => (
                <p key={idx} className={styles["error-text"]}>
                  {msg}
                </p>
              ))}
            </div>
           )}
            <h4>Links – gespeicherte Befunde:</h4>
            <div className={styles.matrix}>
              {data.medLeft.map((item, index) => (
                <div key={index} className={styles.savedItem}>
                  <span>{item.loc} – {item.befund}</span>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => deleteFinding("medLeft", index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div> )}
      </div> 

      {/* Divider before Lateral */}
      <div className={styles.sectionDivider}></div>

      {/* ---------------------- LATERAL ---------------------- */}
      <h2 className={styles.subTitle}>Lateral</h2>
      <div className={styles.container}>
        {showRightFoot && (
        <div className={styles.footSection}>
          <img src={lateralR} alt="lateral right" className={styles.lateralImage} />
          <select value={latRightLoc} onChange={e => setLatRightLoc(e.target.value)} className={styles.dropdown}>
            <option value="">Lokalisation</option>
            {lateralRight.map(o => (
              <option 
                key={o} 
                value={o}
                disabled={isRightAmputated && o === "VRL"}
              >
                {o}
              </option>
            ))}
          </select>
          <select
            value={latRightBefund}
            onChange={e => setLatRightBefund(e.target.value)}
            className={styles.dropdown}
          >
            <option value="">Befund</option>
            {getBefundOptions().map(o => (
              <option
                key={o}
                value={o}
                disabled={
                  (o === "Ulcus akut"      && !allowAkutRight) ||
                  (o === "Ulcus abgeheilt" && !allowAbgeheiltRight) ||
                  (blockAllExceptHVandKrallenRechts && !["Hallux valgus", "Krallen-/Hammerzehe"].includes(o)) ||
                  (o === "Hallux valgus"   && latRightLoc !== "M1") ||
                  (o === "Krallen-/Hammerzehe" && !["D1","D2","D3","D4","D5"].includes(latRightLoc)) ||
                  (blockHVandKrallenRechts && (o === "Hallux valgus" || o === "Krallen-/Hammerzehe"))
                }
              >
                {o}
              </option>
            ))}
          </select>
          <button
            className={styles.saveButton}
            disabled={!latRightLoc || !latRightBefund}
            onClick={() => {
              saveFinding("latRight", latRightLoc, latRightBefund);
              setLatRightLoc("");
              setLatRightBefund("");
            }}
          >
            Speichern
          </button>
          <div className={styles.savedList}>
            <p className={styles.hint}>Hinweis: Pro Lokalisation können mehrere Befunde gespeichert werden</p>
            {rightErrorMessages.length > 0 && (
            <div className={styles["validation-errors"]}>
              {rightErrorMessages.map((msg, idx) => (
                <p key={idx} className={styles["error-text"]}>
                  {msg}
                </p>
              ))}
          </div>
          )}  
            <h4>Rechts – gespeicherte Befunde:</h4>
            <div className={styles.matrix}>
              {data.latRight.map((item, index) => (
                <div key={index} className={styles.savedItem}>
                  <span>{item.loc} – {item.befund}</span>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => deleteFinding("latRight", index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div> 
        </div> )}
        
        {showLeftFoot && (
        <div className={styles.footSection}>
          <img src={lateralL} alt="lateral left" className={styles.lateralImage} />
          <select value={latLeftLoc} onChange={e => setLatLeftLoc(e.target.value)} className={styles.dropdown}>
            <option value="">Lokalisation</option>
            {lateralLeft.map(o => (
              <option 
                key={o} 
                value={o}
                disabled={isLeftAmputated && o === "VLL"}
              >
                {o}
              </option>
            ))}
          </select>
          <select
            value={latLeftBefund}
            onChange={e => setLatLeftBefund(e.target.value)}
            className={styles.dropdown}
          >
            <option value="">Befund</option>
            {getBefundOptions().map(o => (
              <option
                key={o}
                value={o}
                disabled={
                  (o === "Ulcus akut"      && !allowAkutLeft) ||
                  (o === "Ulcus abgeheilt" && !allowAbgeheiltLeft) ||
                  (blockAllExceptHVandKrallenLinks && !["Hallux valgus", "Krallen-/Hammerzehe"].includes(o)) ||
                  (o === "Hallux valgus"   && latLeftLoc !== "M1") ||
                  (o === "Krallen-/Hammerzehe" && !["D1","D2","D3","D4","D5"].includes(latLeftLoc)) ||
                  (blockHVandKrallenLinks && (o === "Hallux valgus" || o === "Krallen-/Hammerzehe"))
                }
              >
                {o}
              </option>
            ))}
          </select>
          <button
            className={styles.saveButton}
            disabled={!latLeftLoc || !latLeftBefund}
            onClick={() => {
              saveFinding("latLeft", latLeftLoc, latLeftBefund);
              setLatLeftLoc("");
              setLatLeftBefund("");
            }}
          >
            Speichern
          </button>
          <div className={styles.savedList}>
            <p className={styles.hint}>Hinweis: Pro Lokalisation können mehrere Befunde gespeichert werden</p>
            {leftErrorMessages.length > 0 && (
            <div className={styles["validation-errors"]}>
              {leftErrorMessages.map((msg, idx) => (
                <p key={idx} className={styles["error-text"]}>
                  {msg}
                </p>
              ))}
            </div>
           )}
            <h4>Links – gespeicherte Befunde:</h4>
            <div className={styles.matrix}>
              {data.latLeft.map((item, index) => (
                <div key={index} className={styles.savedItem}>
                  <span>{item.loc} – {item.befund}</span>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => deleteFinding("latLeft", index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div> )}
      </div>

      {/* Divider before Plantar */}
      <div className={styles.sectionDivider}></div>

      {/* ---------------------- PLANTAR ---------------------- */}
      <h2 className={styles.subTitle}>Plantar</h2>
      <div className={styles.container}>
        {showRightFoot && (
        <div className={styles.footSection}>
          <img src={plantarR} alt="plantar right" className={styles.plantarImage} />
          <select value={plRightLoc} onChange={e => setPlRightLoc(e.target.value)} className={styles.dropdown}>
            <option value="">Lokalisation</option>
{plantarRight.map(o => (
  <option 
    key={o} 
    value={o}
    disabled={
      (isRightAmputated && (["D1","D2","D3","D4","D5","M1","M2","M3","M4","M5"].includes(o))) ||
      (["D1","D2","D3","D4","D5"].includes(o) && isToeAmputated("right", o))
    }
  >
    {o}
  </option>
))}
          </select>
          <select
            value={plRightBefund}
            onChange={e => setPlRightBefund(e.target.value)}
            className={styles.dropdown}
          >
            <option value="">Befund</option>
            {getBefundOptions(true).map(o => (
              <option
                key={o}
                value={o}
                disabled={
                  (o === "Ulcus akut"      && !allowAkutRight) ||
                  (o === "Ulcus abgeheilt" && !allowAbgeheiltRight) ||
                  (blockAllExceptHVandKrallenRechts && !["Hallux valgus", "Krallen-/Hammerzehe"].includes(o)) ||
                  (o === "Hallux valgus"   && plRightLoc !== "M1") ||
                  (o === "Krallen-/Hammerzehe" && !["D1","D2","D3","D4","D5"].includes(plRightLoc)) ||
                  (blockHVandKrallenRechts && (o === "Hallux valgus" || o === "Krallen-/Hammerzehe")) ||
                  getUlcusDisabled(o, plRightLoc, "plRight")
                }
              >
                {o}
              </option>
            ))}
          </select>
          <button
            className={styles.saveButton}
            disabled={!plRightLoc || !plRightBefund}
            onClick={() => {
              saveFinding("plRight", plRightLoc, plRightBefund);
              setPlRightLoc("");
              setPlRightBefund("");
            }}
          >
            Speichern
          </button>
          <div className={styles.savedList}>
            <p className={styles.hint}>Hinweis: Pro Lokalisation können mehrere Befunde gespeichert werden</p>
            {rightErrorMessages.length > 0 && (
            <div className={styles["validation-errors"]}>
              {rightErrorMessages.map((msg, idx) => (
                <p key={idx} className={styles["error-text"]}>
                  {msg}
                </p>
              ))}
          </div>
          )}  
            <h4>Rechts – gespeicherte Befunde:</h4>
            <div className={styles.matrix}>
              {data.plRight.map((item, index) => (
                <div key={index} className={styles.savedItem}>
                  <span>{item.loc} – {item.befund}</span>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => deleteFinding("plRight", index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div> 
        </div> )}
        
        {showLeftFoot && (
        <div className={styles.footSection}>
          <img src={plantarL} alt="plantar left" className={styles.plantarImage} />
          <select value={plLeftLoc} onChange={e => setPlLeftLoc(e.target.value)} className={styles.dropdown}>
            <option value="">Lokalisation</option>
{plantarLeft.map(o => (
  <option 
    key={o} 
    value={o}
    disabled={
      (isLeftAmputated && (["D1","D2","D3","D4","D5","M1","M2","M3","M4","M5"].includes(o))) ||
      (["D1","D2","D3","D4","D5"].includes(o) && isToeAmputated("left", o))
    }
  >
    {o}
  </option>
))}
          </select>
          <select
            value={plLeftBefund}
            onChange={e => setPlLeftBefund(e.target.value)}
            className={styles.dropdown}
          >
            <option value="">Befund</option>
            {getBefundOptions(true).map(o => (
              <option
                key={o}
                value={o}
                disabled={
                  (o === "Ulcus akut"      && !allowAkutLeft) ||
                  (o === "Ulcus abgeheilt" && !allowAbgeheiltLeft) ||
                  (blockAllExceptHVandKrallenLinks && !["Hallux valgus", "Krallen-/Hammerzehe"].includes(o)) ||
                  (o === "Hallux valgus"   && plLeftLoc !== "M1") ||
                  (o === "Krallen-/Hammerzehe" && !["D1","D2","D3","D4","D5"].includes(plLeftLoc)) ||
                  (blockHVandKrallenLinks && (o === "Hallux valgus" || o === "Krallen-/Hammerzehe")) ||
                  getUlcusDisabled(o, plLeftLoc, "plLeft")
                }
              >
                {o}
              </option>
            ))}
          </select>
          <button
            className={styles.saveButton}
            disabled={!plLeftLoc || !plLeftBefund}
            onClick={() => {
              saveFinding("plLeft", plLeftLoc, plLeftBefund);
              setPlLeftLoc("");
              setPlLeftBefund("");
            }}
          >
            Speichern
          </button>
          <div className={styles.savedList}>
            <p className={styles.hint}>Hinweis: Pro Lokalisation können mehrere Befunde gespeichert werden</p>
            {leftErrorMessages.length > 0 && (
            <div className={styles["validation-errors"]}>
              {leftErrorMessages.map((msg, idx) => (
                <p key={idx} className={styles["error-text"]}>
                  {msg}
                </p>
              ))}
            </div>
           )}
            <h4>Links – gespeicherte Befunde:</h4>
            <div className={styles.matrix}>
              {data.plLeft.map((item, index) => (
                <div key={index} className={styles.savedItem}>
                  <span>{item.loc} – {item.befund}</span>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => deleteFinding("plLeft", index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div> 
        </div>  )}
      </div> 

      {/* Divider before Dorsal */}
      <div className={styles.sectionDivider}></div>

      {/* ---------------------- DORSAL ---------------------- */}
      <h2 className={styles.subTitle}>Dorsal</h2>
      <div className={styles.container}>
        {showRightFoot && (
        <div className={styles.footSection}>
          <img src={dorsalR} alt="dorsal right" className={styles.dorsalImage} />
          <select value={dorsRightLoc} onChange={e => setDorsRightLoc(e.target.value)} className={styles.dropdown}>
            <option value="">Lokalisation</option>
{dorsalRight.map(o => (
  <option 
    key={o} 
    value={o}
    disabled={
      (isRightAmputated && ["D1","D2","D3","D4","D5","M1","M2","M3","M4","M5"].includes(o)) ||
      (["D1","D2","D3","D4","D5"].includes(o) && isToeAmputated("right", o))
    }
  >
    {o}
  </option>
))}
          </select>
          <select
            value={dorsRightBefund}
            onChange={e => setDorsRightBefund(e.target.value)}
            className={styles.dropdown}
          >
            <option value="">Befund</option>
            {getBefundOptions().map(o => (
              <option
                key={o}
                value={o}
                disabled={
                  (o === "Ulcus akut"      && !allowAkutRight) ||
                  (o === "Ulcus abgeheilt" && !allowAbgeheiltRight) ||
                  (blockAllExceptHVandKrallenRechts && !["Hallux valgus", "Krallen-/Hammerzehe"].includes(o)) ||
                  (o === "Hallux valgus"   && dorsRightLoc !== "M1") ||
                  (o === "Krallen-/Hammerzehe" && !["D1","D2","D3","D4","D5"].includes(dorsRightLoc)) ||
                  (blockHVandKrallenRechts && (o === "Hallux valgus" || o === "Krallen-/Hammerzehe")) ||
                  getUlcusDisabled(o, dorsRightLoc, "dorsRight")
                }
              >
                {o}
              </option>
            ))}
          </select>
          <button
            className={styles.saveButton}
            disabled={!dorsRightLoc || !dorsRightBefund}
            onClick={() => {
              saveFinding("dorsRight", dorsRightLoc, dorsRightBefund);
              setDorsRightLoc("");
              setDorsRightBefund("");
            }}
          >
            Speichern
          </button>
          <div className={styles.savedList}>
            <p className={styles.hint}>Hinweis: Pro Lokalisation können mehrere Befunde gespeichert werden</p>
            {rightErrorMessages.length > 0 && (
            <div className={styles["validation-errors"]}>
              {rightErrorMessages.map((msg, idx) => (
                <p key={idx} className={styles["error-text"]}>
                  {msg}
                </p>
              ))}
          </div>
          )}  
            <h4>Rechts – gespeicherte Befunde:</h4>
            <div className={styles.matrix}>
              {data.dorsRight.map((item, index) => (
                <div key={index} className={styles.savedItem}>
                  <span>{item.loc} – {item.befund}</span>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => deleteFinding("dorsRight", index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div> 
        </div> )}


        {showLeftFoot && (
        <div className={styles.footSection}>
          <img src={dorsalL} alt="dorsal left" className={styles.dorsalImage} />
          <select value={dorsLeftLoc} onChange={e => setDorsLeftLoc(e.target.value)} className={styles.dropdown}>
            <option value="">Lokalisation</option>
{dorsalLeft.map(o => (
  <option 
    key={o} 
    value={o}
    disabled={
      (isLeftAmputated && ["D1","D2","D3","D4","D5","M1","M2","M3","M4","M5"].includes(o)) ||
      (["D1","D2","D3","D4","D5"].includes(o) && isToeAmputated("left", o))
    }
  >
    {o}
  </option>
))}
          </select>
          <select
            value={dorsLeftBefund}
            onChange={e => setDorsLeftBefund(e.target.value)}
            className={styles.dropdown}
          >
            <option value="">Befund</option>
            {getBefundOptions().map(o => (
              <option
                key={o}
                value={o}
                disabled={
                  (o === "Ulcus akut"      && !allowAkutLeft) ||
                  (o === "Ulcus abgeheilt" && !allowAbgeheiltLeft) ||
                  (blockAllExceptHVandKrallenLinks && !["Hallux valgus", "Krallen-/Hammerzehe"].includes(o)) ||
                  (o === "Hallux valgus"   && dorsLeftLoc !== "M1") ||
                  (o === "Krallen-/Hammerzehe" && !["D1","D2","D3","D4","D5"].includes(dorsLeftLoc)) ||
                  (blockHVandKrallenLinks && (o === "Hallux valgus" || o === "Krallen-/Hammerzehe")) ||
                  getUlcusDisabled(o, dorsLeftLoc, "dorsLeft")
                }
              >
                {o}
              </option>
            ))}
          </select>
          <button
            className={styles.saveButton}
            disabled={!dorsLeftLoc || !dorsLeftBefund}
            onClick={() => {
              saveFinding("dorsLeft", dorsLeftLoc, dorsLeftBefund);
              setDorsLeftLoc("");
              setDorsLeftBefund("");
            }}
          >
            Speichern
          </button>
          <div className={styles.savedList}>
            <p className={styles.hint}>Hinweis: Pro Lokalisation können mehrere Befunde gespeichert werden</p>
            {leftErrorMessages.length > 0 && (
            <div className={styles["validation-errors"]}>
              {leftErrorMessages.map((msg, idx) => (
                <p key={idx} className={styles["error-text"]}>
                  {msg}
                </p>
              ))}
            </div>
           )}
            <h4>Links – gespeicherte Befunde:</h4>
            <div className={styles.matrix}>
              {data.dorsLeft.map((item, index) => (
                <div key={index} className={styles.savedItem}>
                  <span>{item.loc} – {item.befund}</span>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => deleteFinding("dorsLeft", index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div> )}
      </div>

{/* ──────────────── ACTIONS (Zurück / Weiter) ──────────────── */}
      <div className={`${styles.row} ${styles.actions}`}>
        <button
          type="button"
          className={styles["btn-primary"]}
          onClick={() => navigate("/fussstatus")}
        >
          Zurück
        </button>
        <button
          type="button"
          className={styles["btn-primary"]}
          onClick={() => navigate("/kriterien")}
          disabled={hasErrors}
        >
          Weiter
        </button>
      </div>

    </div>
  );
};

export default FussstatusGrafik;